import React from 'react';
import classNames from 'classnames';
import styles from './RequestDetailTab.module.scss';
import { useQuery } from 'react-query';
import { Spinner } from 'react-bootstrap';
import {
  DataVendor,
  DjangoListResponse,
  SearchEntity,
  SearchFilterType,
  SearchQuestionEntity,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import { parseSearchFilter } from 'v2/utils/searchUtils';
import MarkdownRender from 'components/MarkdownRender/MarkdownRender';
import SearchFilter from 'v2/pages/SearchDetails/SearchFilter';
import SectionHeader from '../../SectionHeader/SectionHeader';

interface RequestDetailTabProps {
  request: SearchEntity;
  dataVendor: DataVendor;
}

const RequestDetailTab = ({ request }: RequestDetailTabProps) => {
  const { data: filters, isLoading: isFiltersLoading } = useQuery(
    ['Get filters related to request by ID', request.id],
    async () => {
      const response = await restFactory.get<
        DjangoListResponse<SearchFilterType>
      >(`/dj/api/v1/search/${request.id}/filters/`, { size: 100 });

      return response.results;
    },
    { cacheTime: 0, refetchOnWindowFocus: false }
  );

  const { data: questions, isLoading: isQuestionsLoading } = useQuery(
    ['Get questions related to request by ID', request.id],
    async () => {
      const response = await restFactory.get<
        DjangoListResponse<SearchQuestionEntity>
      >(`/dj/api/v1/search/${request.id}/questions/`, { size: 100 });
      return response.results;
    },
    { cacheTime: 0, refetchOnWindowFocus: false }
  );

  if (isFiltersLoading || isQuestionsLoading) {
    return (
      <div className={classNames(styles.loading)} aria-label="loading-spinner">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div>
      <div className={classNames(styles.section, 'pt-5')}>
        <SectionHeader title="Originator" className="mb-3" />
        <p className={classNames(styles.originatorName, 'm-0')}>
          {`${request.submitted_by?.first_name} ${request.submitted_by?.last_name}`}
        </p>
        <p className="m-0">{request.submitted_by?.job_title}</p>
        <p className="m-0">{request.organization?.name}</p>
      </div>

      <div className={classNames(styles.section)}>
        <SectionHeader title="Description" className="mb-3" />
        <MarkdownRender
          className="markdown-text-blue-light pr-4"
          source={request.description}
          format={false}
          preserveSpaces
        />
      </div>

      {questions && questions.length > 0 && (
        <div className={classNames(styles.section)}>
          {questions.map((item, index) => (
            <div key={item.id} className="d-flex align-items-center mb-4">
              <span className={classNames('my-0 pr-2', styles.questionNumber)}>
                {`${index < 10 ? '0' : ''}${index + 1}`}
              </span>
              <label className={classNames('my-0 pl-2', styles.questionText)}>
                {item.name}
              </label>
            </div>
          ))}
        </div>
      )}

      <div className={classNames(styles.section)}>
        <SectionHeader title="Shared identity?" className="mb-2" />
        <p>{request.hide_identity ? 'No' : 'Yes'}</p>
      </div>

      {filters?.map((item) => (
        <div
          key={item.filter.name}
          className={classNames(styles.section, styles.filter)}
        >
          <SearchFilter
            label={item.filter.name}
            value={parseSearchFilter(
              item.data.filter_value,
              item.data.filter_units
            )}
          />
        </div>
      ))}
    </div>
  );
};

export default RequestDetailTab;
